/**
 * Shared coverage note hooks
 *
 * These hooks provide React Query integration for BTX coverage note endpoints.
 * They support cache sharing across feature libraries and optimistic updates.
 *
 * Uses BTX endpoints:
 * - GET    /btx/v1/rfps/{rfpId}/coverages/{coverageId}/note
 * - PUT    /btx/v1/rfps/{rfpId}/coverages/{coverageId}/note
 * - DELETE /btx/v1/rfps/{rfpId}/coverages/{coverageId}/note
 */

import {
  useQuery,
  useMutation,
  useQueryClient,
  UseQueryResult,
  UseMutationResult,
} from "@tanstack/react-query";
import { toMinutes, useApi } from "@platform/api";

import {
  CoverageNoteResponse,
  UpsertCoverageNoteRequest,
  CoverageNotesMap,
} from "./types";

const COVERAGE_NOTES_QUERY_KEY = "coverage-notes";

/**
 * Resolves the stable map key for a coverage note.
 *
 * Notes are addressed by (coverageId, benefitPlanType), so plans that share a
 * coverage id (e.g. Dental PPO/HMO) must map to distinct keys. Callers may pass
 * an explicit `noteKey`; when absent we derive one from `benefitPlanType` so
 * multi-plan coverages don't collide under the shared id. Single-plan coverages
 * (no `benefitPlanType`) keep `id` as the key.
 */
export const resolveNoteKey = (coverage: {
  id: string;
  benefitPlanType?: string;
  noteKey?: string;
}): string =>
  coverage.noteKey ??
  (coverage.benefitPlanType
    ? `${coverage.id}:${coverage.benefitPlanType}`
    : coverage.id);

// ============================================================================
// Query Keys
// ============================================================================

/**
 * Query keys for coverage note caching
 *
 * Exported for cross-library cache sharing and invalidation.
 */
export const coverageNoteQueryKeys = {
  /** Key for all coverage notes in an RFP */
  coverageNotes: (
    ecoRfpId: string,
    coverages: {
      id: string;
      benefitPlanType?: string;
      noteKey?: string;
    }[] = [],
  ): ["send-rfp", string, "coverage-notes", string] => [
    "send-rfp",
    ecoRfpId,
    COVERAGE_NOTES_QUERY_KEY,
    serializeCoverageScope(coverages),
  ],
  draftCoverageNotes: (
    ecoRfpId: string,
  ): ["send-rfp", string, "coverage-note-drafts"] => [
    "send-rfp",
    ecoRfpId,
    "coverage-note-drafts",
  ],
  baselineCoverageNotes: (
    ecoRfpId: string,
  ): ["send-rfp", string, "baseline-coverage-notes"] => [
    "send-rfp",
    ecoRfpId,
    "baseline-coverage-notes",
  ],
};

// ============================================================================
// Client Functions
// ============================================================================

/**
 * Builds the URL for a coverage note endpoint, optionally including benefitPlanType
 */
const buildCoverageNoteUrl = (
  rfpId: string,
  coverageId: string,
  benefitPlanType?: string,
): string => {
  const base = `btx/v1/rfps/${rfpId}/coverages/${coverageId}/note`;
  return benefitPlanType
    ? `${base}?benefitPlanType=${encodeURIComponent(benefitPlanType)}`
    : base;
};

/**
 * Fetches the note for a specific coverage
 */
const getCoverageNote = async (
  rfpId: string,
  coverageId: string,
  axiosInstance: { get: <T>(url: string) => Promise<{ data: T }> },
  benefitPlanType?: string,
): Promise<CoverageNoteResponse> => {
  try {
    const response = await axiosInstance.get<CoverageNoteResponse>(
      buildCoverageNoteUrl(rfpId, coverageId, benefitPlanType),
    );
    return response.data;
  } catch (error) {
    if (
      typeof error === "object" &&
      error !== null &&
      "response" in error &&
      (error as { response?: { status?: number } }).response?.status === 404
    ) {
      return { content: "" };
    }

    throw error;
  }
};

/**
 * Creates or updates the note for a specific coverage
 */
const upsertCoverageNote = async (
  rfpId: string,
  coverageId: string,
  request: UpsertCoverageNoteRequest,
  axiosInstance: {
    put: <T>(url: string, data: unknown) => Promise<{ data: T }>;
  },
  benefitPlanType?: string,
): Promise<CoverageNoteResponse> => {
  const response = await axiosInstance.put<CoverageNoteResponse>(
    buildCoverageNoteUrl(rfpId, coverageId, benefitPlanType),
    request,
  );
  return response.data;
};

/**
 * Deletes (soft-deletes) the note for a specific coverage
 */
const deleteCoverageNote = async (
  rfpId: string,
  coverageId: string,
  axiosInstance: { delete: (url: string) => Promise<unknown> },
  benefitPlanType?: string,
): Promise<void> => {
  await axiosInstance.delete(
    buildCoverageNoteUrl(rfpId, coverageId, benefitPlanType),
  );
};

// ============================================================================
// Query Hooks
// ============================================================================

interface UseCoverageNotesOptions {
  ecoRfpId: string;
  coverages: { id: string; benefitPlanType?: string; noteKey?: string }[];
  enabled?: boolean;
}

const serializeCoverageScope = (
  coverages: { id: string; benefitPlanType?: string }[],
): string =>
  JSON.stringify(
    coverages
      .map(({ id, benefitPlanType }) => [id, benefitPlanType ?? ""])
      .sort(([leftId, leftPlan], [rightId, rightPlan]) => {
        const leftKey = `${leftId}:${leftPlan}`;
        const rightKey = `${rightId}:${rightPlan}`;

        return leftKey.localeCompare(rightKey);
      }),
  );

/**
 * Hook to fetch notes for all coverages in an RFP
 *
 * Fetches notes for each coverage in parallel and returns them as a map
 * keyed by coverage ID. Returns empty string for coverages without notes.
 *
 * @param options.ecoRfpId - The BTX (eco) RFP ID
 * @param options.coverages - Array of coverages with IDs to fetch notes for
 * @param options.enabled - Whether the query should execute (default: true)
 * @returns Query result with coverage notes map
 *
 * @example
 * ```tsx
 * const { data: notes } = useCoverageNotes({
 *   ecoRfpId: "eco-123",
 *   coverages: [{ id: "cov-1" }, { id: "cov-2" }],
 * });
 * // notes = { "cov-1": "Note text", "cov-2": "" }
 * ```
 */
export const useCoverageNotes = ({
  ecoRfpId,
  coverages,
  enabled = true,
}: UseCoverageNotesOptions): UseQueryResult<CoverageNotesMap, Error> => {
  const axiosInstance = useApi();

  return useQuery({
    queryKey: coverageNoteQueryKeys.coverageNotes(ecoRfpId, coverages),
    queryFn: async () => {
      const notes = await Promise.allSettled(
        coverages.map(async (coverage) => {
          const note = await getCoverageNote(
            ecoRfpId,
            coverage.id,
            axiosInstance,
            coverage.benefitPlanType,
          );

          return [resolveNoteKey(coverage), note.content] as const;
        }),
      );

      const rejectedNotes = notes.filter(
        (result): result is PromiseRejectedResult =>
          result.status === "rejected",
      );

      if (
        rejectedNotes.length === coverages.length &&
        rejectedNotes.length > 0
      ) {
        throw rejectedNotes[0].reason instanceof Error
          ? rejectedNotes[0].reason
          : new Error("Failed to fetch coverage notes");
      }

      return Object.fromEntries(
        notes.map((result, index) => {
          if (result.status === "fulfilled") {
            return result.value;
          }

          return [resolveNoteKey(coverages[index]), ""] as const;
        }),
      );
    },
    enabled: enabled && !!ecoRfpId && coverages.length > 0,
    staleTime: toMinutes(5),
  });
};

// ============================================================================
// Mutation Hooks
// ============================================================================

interface UseFetchCoverageNoteOptions {
  ecoRfpId: string;
  benefitPlanType?: string;
  onSuccess?: (data: CoverageNoteResponse) => void;
  onError?: (error: Error) => void;
}

/**
 * Hook to fetch a single coverage note
 *
 * Returns a mutation that fetches a note for a specific coverage.
 * Useful when you need to fetch a note on-demand rather than as a query.
 *
 * @param options.ecoRfpId - The BTX (eco) RFP ID
 * @param options.onSuccess - Callback on successful fetch
 * @param options.onError - Callback on error
 * @returns Mutation result
 *
 * @example
 * ```tsx
 * const { mutate: fetchNote } = useFetchCoverageNote({ ecoRfpId: "eco-123" });
 * fetchNote("cov-1");
 * ```
 */
export const useFetchCoverageNote = ({
  ecoRfpId,
  benefitPlanType,
  onSuccess,
  onError,
}: UseFetchCoverageNoteOptions): UseMutationResult<
  CoverageNoteResponse,
  Error,
  string
> => {
  const axiosInstance = useApi();

  return useMutation({
    mutationFn: (coverageId: string) =>
      getCoverageNote(ecoRfpId, coverageId, axiosInstance, benefitPlanType),
    onSuccess,
    onError,
  });
};

interface UpsertCoverageNoteParams {
  coverageId: string;
  content: string;
  benefitPlanType?: string;
}

interface UseUpsertCoverageNoteOptions {
  ecoRfpId: string;
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}

/**
 * Hook to upsert a coverage note
 *
 * Optimistically updates the coverage notes cache on success.
 *
 * @param options.ecoRfpId - The BTX (eco) RFP ID
 * @param options.onSuccess - Callback on successful upsert
 * @param options.onError - Callback on error
 * @returns Mutation result
 *
 * @example
 * ```tsx
 * const { mutate: upsert } = useUpsertCoverageNote({ ecoRfpId: "eco-123" });
 * upsert({ coverageId: "cov-1", content: "Updated note" });
 * ```
 */
export const useUpsertCoverageNote = ({
  ecoRfpId,
  onSuccess,
  onError,
}: UseUpsertCoverageNoteOptions): UseMutationResult<
  CoverageNoteResponse,
  Error,
  UpsertCoverageNoteParams
> => {
  const axiosInstance = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      coverageId,
      content,
      benefitPlanType,
    }: UpsertCoverageNoteParams) =>
      upsertCoverageNote(
        ecoRfpId,
        coverageId,
        { content },
        axiosInstance,
        benefitPlanType,
      ),
    onSuccess: (_data, { coverageId, content, benefitPlanType }) => {
      const noteKey = resolveNoteKey({ id: coverageId, benefitPlanType });
      queryClient.setQueriesData<CoverageNotesMap>(
        { queryKey: ["send-rfp", ecoRfpId, COVERAGE_NOTES_QUERY_KEY] },
        (prev) =>
          prev ? { ...prev, [noteKey]: content } : { [noteKey]: content },
      );
      queryClient.setQueryData<CoverageNotesMap>(
        coverageNoteQueryKeys.draftCoverageNotes(ecoRfpId),
        (prev) => {
          if (!prev) {
            return { [noteKey]: content };
          }

          if (prev[noteKey] !== undefined && prev[noteKey] !== content) {
            return prev;
          }

          return { ...prev, [noteKey]: content };
        },
      );
      queryClient.setQueryData<CoverageNotesMap>(
        coverageNoteQueryKeys.baselineCoverageNotes(ecoRfpId),
        (prev) =>
          prev ? { ...prev, [noteKey]: content } : { [noteKey]: content },
      );
      onSuccess?.();
    },
    onError,
  });
};

interface UseDeleteCoverageNoteOptions {
  ecoRfpId: string;
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}

interface DeleteCoverageNoteParams {
  coverageId: string;
  benefitPlanType?: string;
}

/**
 * Hook to delete a coverage note
 *
 * Optimistically clears the note in the coverage notes cache on success.
 *
 * @param options.ecoRfpId - The BTX (eco) RFP ID
 * @param options.onSuccess - Callback on successful delete
 * @param options.onError - Callback on error
 * @returns Mutation result
 *
 * @example
 * ```tsx
 * const { mutate: remove } = useDeleteCoverageNote({ ecoRfpId: "eco-123" });
 * remove({ coverageId: "cov-1" });
 * ```
 */
export const useDeleteCoverageNote = ({
  ecoRfpId,
  onSuccess,
  onError,
}: UseDeleteCoverageNoteOptions): UseMutationResult<
  void,
  Error,
  DeleteCoverageNoteParams
> => {
  const axiosInstance = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ coverageId, benefitPlanType }: DeleteCoverageNoteParams) =>
      deleteCoverageNote(ecoRfpId, coverageId, axiosInstance, benefitPlanType),
    onSuccess: (_data, params) => {
      const { coverageId, benefitPlanType } = params;
      const noteKey = resolveNoteKey({ id: coverageId, benefitPlanType });

      queryClient.setQueriesData<CoverageNotesMap>(
        { queryKey: ["send-rfp", ecoRfpId, COVERAGE_NOTES_QUERY_KEY] },
        (prev) => {
          if (!prev) return prev;
          const updated = { ...prev };
          updated[noteKey] = "";
          return updated;
        },
      );
      queryClient.setQueryData<CoverageNotesMap>(
        coverageNoteQueryKeys.draftCoverageNotes(ecoRfpId),
        (prev) => {
          if (!prev) return prev;
          const updated = { ...prev };
          updated[noteKey] = "";
          return updated;
        },
      );
      queryClient.setQueryData<CoverageNotesMap>(
        coverageNoteQueryKeys.baselineCoverageNotes(ecoRfpId),
        (prev) => {
          if (!prev) return prev;
          const updated = { ...prev };
          updated[noteKey] = "";
          return updated;
        },
      );
      onSuccess?.();
    },
    onError,
  });
};
